import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import type {
  Invite,
  InviteListItem,
  InviteGuest,
  InviteThemePreset,
  InviteVisibility,
  CreateInviteData,
  UpdateInviteData,
  AddGuestData,
  BulkImportGuestsData,
  PublicInviteData,
  SubmitRsvpData,
  GuestWithInvite,
  InviteTranslation,
  InviteTranslatableContent,
  RsvpSummary,
  GuestTag,
} from '../../types';

export interface AllergyDataSummary {
  invite_id: number;
  title: string;
  event_date: string | null;
  guest_count: number;
  retention_requested: boolean;
}

/**
 * Hook to get all invites for the current team
 */
export function useInvites() {
  return useQuery({
    queryKey: ['invites'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: InviteListItem[] }>('/api/v1/invites');
      return response.data.data;
    },
  });
}

/**
 * Hook to get a single invite with full details
 */
export function useInvite(inviteId: number | undefined) {
  return useQuery({
    queryKey: ['invite', inviteId],
    queryFn: async () => {
      const response = await apiClient.get<Invite>(`/api/v1/invites/${inviteId}`);
      return response.data;
    },
    enabled: !!inviteId,
  });
}

/**
 * Hook to create a new invite
 */
export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInviteData) => {
      const response = await apiClient.post<{ invite: Invite }>('/api/v1/invites', data);
      return response.data.invite;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

/**
 * Hook to update an invite
 */
export function useUpdateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, data }: { inviteId: number; data: UpdateInviteData }) => {
      const response = await apiClient.patch<{ invite: Invite }>(`/api/v1/invites/${inviteId}`, data);
      return response.data.invite;
    },
    onSuccess: (updatedInvite, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      // Keep the individual invite cache fresh so re-entering the builder
      // never shows stale data (old illustration, theme, etc.)
      queryClient.setQueryData(['invite', inviteId], updatedInvite);
    },
  });
}

/**
 * Hook to delete an invite
 */
export function useDeleteInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: number) => {
      const response = await apiClient.delete(`/api/v1/invites/${inviteId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

/**
 * Hook to duplicate an invite
 */
export function useDuplicateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: number) => {
      const response = await apiClient.post<{ data: Invite }>(`/api/v1/invites/${inviteId}/duplicate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

/**
 * Hook to publish an invite
 */
export function usePublishInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: number) => {
      const response = await apiClient.post<{ invite?: Invite; error_code?: string; message?: string; price?: number; full_price?: number; has_promo?: boolean }>(`/api/v1/invites/${inviteId}/publish`);
      return response.data;
    },
    onSuccess: (data, inviteId) => {
      if (!data.error_code) {
        queryClient.invalidateQueries({ queryKey: ['invites'] });
        queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
      }
    },
  });
}

/**
 * Hook to unpublish an invite
 */
export function useUnpublishInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: number) => {
      const response = await apiClient.post<{ invite: Invite }>(`/api/v1/invites/${inviteId}/unpublish`);
      return response.data.invite;
    },
    onSuccess: (_, inviteId) => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
    },
  });
}

/**
 * Hook to update an invite's visibility (public/private)
 */
export function useUpdateVisibility() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, visibility }: { inviteId: number; visibility: InviteVisibility }) => {
      const response = await apiClient.patch<{ invite: Invite }>(`/api/v1/invites/${inviteId}/visibility`, { visibility });
      return response.data.invite;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
    },
  });
}

/**
 * Hook to upload an image for an invite
 */
export function useUploadInviteImage() {
  return useMutation({
    mutationFn: async ({ inviteId, file, section, theme }: { inviteId: number; file: File; section: 'hero' | 'gallery' | 'monogram'; theme?: string }) => {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('section', section);
      if (theme) formData.append('theme', theme);

      const response = await apiClient.post<{ image: { id: number; section: string; url: string; order: number; metadata: Record<string, unknown> } }>(
        `/api/v1/invites/${inviteId}/images`,
        formData,
        { headers: { 'Content-Type': undefined } }
      );
      return response.data.image;
    },
  });
}

/**
 * Hook to delete an image
 */
export function useDeleteInviteImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, imageId }: { inviteId: number; imageId: number }) => {
      const response = await apiClient.delete(`/api/v1/invites/${inviteId}/images/${imageId}`);
      return response.data;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
    },
  });
}

/**
 * Hook to generate a sketch version of a photo via AI
 */
export function useGenerateSketch() {
  return useMutation({
    mutationFn: async ({ inviteId, file }: { inviteId: number; file: File }) => {
      const formData = new FormData();
      formData.append('image', file);

      const response = await apiClient.post<{ image: { id: number; section: string; url: string }; remove_bg: boolean }>(
        `/api/v1/invites/${inviteId}/sketch`,
        formData,
        { headers: { 'Content-Type': undefined } }
      );
      return response.data;
    },
  });
}

/**
 * Hook to reorder gallery images
 */
export function useReorderInviteImages() {
  return useMutation({
    mutationFn: async ({ inviteId, imageIds }: { inviteId: number; imageIds: number[] }) => {
      const response = await apiClient.patch(`/api/v1/invites/${inviteId}/images/reorder`, { image_ids: imageIds });
      return response.data;
    },
  });
}

/**
 * Hook to get all guests for an invite
 */
export function useInviteGuests(inviteId: number | undefined) {
  return useQuery({
    queryKey: ['invite-guests', inviteId],
    queryFn: async () => {
      const response = await apiClient.get<{ data: InviteGuest[] }>(`/api/v1/invites/${inviteId}/guests`);
      return response.data.data;
    },
    enabled: !!inviteId,
  });
}

/**
 * Hook to get all guests across all invites for the current team
 */
export function useAllGuests(params?: { search?: string; rsvp_status?: string; invite_id?: number }) {
  return useQuery({
    queryKey: ['all-guests', params],
    queryFn: async () => {
      const response = await apiClient.get<{ data: GuestWithInvite[] }>('/api/v1/guests', { params });
      return response.data.data;
    },
    refetchInterval: 1000 * 30, // poll every 30s
  });
}

/**
 * Hook to get RSVP summary across all invites
 */
export function useRsvpSummary() {
  return useQuery({
    queryKey: ['rsvp-summary'],
    queryFn: async () => {
      const response = await apiClient.get<RsvpSummary>('/api/v1/guests/rsvp-summary');
      return response.data;
    },
    refetchInterval: 1000 * 30, // poll every 30s
  });
}

/**
 * Hook to assign guests to a different invite
 */
export function useAssignGuestsToInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ guestIds, inviteId }: { guestIds: number[]; inviteId: number }) => {
      const response = await apiClient.post<{ data: GuestWithInvite[] }>('/api/v1/guests/assign-invite', {
        guest_ids: guestIds,
        invite_id: inviteId,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['invite-guests'] });
      queryClient.invalidateQueries({ queryKey: ['rsvp-summary'] });
    },
  });
}

/**
 * Hook to bulk update RSVP status for multiple guests
 */
export function useBulkUpdateRsvp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ guestIds, rsvpStatus }: { guestIds: number[]; rsvpStatus: string }) => {
      const response = await apiClient.post<{ message: string; count: number }>('/api/v1/guests/bulk-update-rsvp', {
        guest_ids: guestIds,
        rsvp_status: rsvpStatus,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['invite-guests'] });
      queryClient.invalidateQueries({ queryKey: ['rsvp-summary'] });
    },
  });
}

/**
 * Hook to add a guest to an invite
 */
export function useAddInviteGuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, data }: { inviteId: number; data: AddGuestData }) => {
      const response = await apiClient.post<{ guest: InviteGuest }>(`/api/v1/invites/${inviteId}/guests`, data);
      return response.data.guest;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-guests', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['rsvp-summary'] });
    },
  });
}

/**
 * Hook to bulk import guests
 */
export function useBulkImportGuests() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, data }: { inviteId: number; data: BulkImportGuestsData }) => {
      const response = await apiClient.post<{ guests: InviteGuest[] }>(`/api/v1/invites/${inviteId}/guests/bulk`, data);
      return response.data.guests;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-guests', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['rsvp-summary'] });
    },
  });
}

/**
 * Hook to update a guest's details
 */
export function useUpdateInviteGuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, guestId, data }: { inviteId: number; guestId: number; data: { name?: string; email?: string; dietary_notes?: string; dietary_preference?: string; allergies?: string[]; rsvp_status?: string; mark_as_invited?: boolean; tag_ids?: number[] } }) => {
      const response = await apiClient.put<{ guest: InviteGuest }>(`/api/v1/invites/${inviteId}/guests/${guestId}`, data);
      return response.data.guest;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-guests', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['rsvp-summary'] });
    },
  });
}

/**
 * Hook to remove a guest from an invite
 */
export function useRemoveInviteGuest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, guestId }: { inviteId: number; guestId: number }) => {
      const response = await apiClient.delete(`/api/v1/invites/${inviteId}/guests/${guestId}`);
      return response.data;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-guests', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['rsvp-summary'] });
    },
  });
}

/**
 * Hook to bulk remove guests from an invite
 */
export function useBulkRemoveGuests() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, guestIds }: { inviteId: number; guestIds: number[] }) => {
      const response = await apiClient.post<{ message: string; count: number }>(
        `/api/v1/invites/${inviteId}/guests/bulk-remove`,
        { guest_ids: guestIds },
      );
      return response.data;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-guests', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
      queryClient.invalidateQueries({ queryKey: ['rsvp-summary'] });
    },
  });
}

/**
 * Hook to send invite email to a single guest
 */
export function useSendGuestEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, guestId }: { inviteId: number; guestId: number }) => {
      const response = await apiClient.post(`/api/v1/invites/${inviteId}/guests/${guestId}/send-email`);
      return response.data;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-guests', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
    },
  });
}

/**
 * Hook to send invite emails to all guests for an invite
 */
export function useSendAllGuestEmails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, resend }: { inviteId: number; resend?: boolean }) => {
      const response = await apiClient.post<{ message: string; count: number }>(
        `/api/v1/invites/${inviteId}/guests/send-all-emails`,
        { resend }
      );
      return response.data;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-guests', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
    },
  });
}

/**
 * Hook to check if a slug is available
 */
export function useCheckSlug(slug: string, inviteId?: number) {
  return useQuery({
    queryKey: ['invites', 'check-slug', slug],
    queryFn: async () => {
      const response = await apiClient.get<{ available: boolean; slug: string }>('/api/v1/invites/check-slug', {
        params: { slug, invite_id: inviteId },
      });
      return response.data;
    },
    enabled: slug.length >= 3,
  });
}

/**
 * Hook to update an invite's slug
 */
export function useUpdateSlug() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inviteId, slug }: { inviteId: number; slug: string }) => {
      const response = await apiClient.patch<{ invite: Invite }>(`/api/v1/invites/${inviteId}/slug`, { slug });
      return response.data.invite;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
    },
  });
}

/**
 * Hook to get theme presets
 */
export function useInviteThemes() {
  return useQuery({
    queryKey: ['invite-themes'],
    queryFn: async () => {
      const response = await apiClient.get<{ presets: InviteThemePreset[] }>('/api/v1/invite-themes');
      return response.data.presets;
    },
  });
}

/**
 * Hook to get public invite data (for guest view)
 */
export function usePublicInvite(identifier: string | undefined) {
  return useQuery({
    queryKey: ['public-invite', identifier],
    queryFn: async () => {
      const response = await apiClient.get<PublicInviteData>(`/api/v1/i/${identifier}`);
      return response.data;
    },
    enabled: !!identifier,
    retry: false,
  });
}

/**
 * Hook to submit RSVP
 */
export function useSubmitRsvp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ identifier, data }: { identifier: string; data: SubmitRsvpData }) => {
      const response = await apiClient.post<{ guest: PublicInviteData['guest'] }>(`/api/v1/i/${identifier}/rsvp`, data);
      return response.data.guest;
    },
    onSuccess: (_, { identifier }) => {
      queryClient.invalidateQueries({ queryKey: ['public-invite', identifier] });
    },
  });
}

/**
 * Hook to track view (fire and forget)
 */
export function useTrackInviteView() {
  return useMutation({
    mutationFn: async (identifier: string) => {
      await apiClient.post(`/api/v1/i/${identifier}/track-view`);
    },
  });
}

/**
 * Hook to get all guest tags for the current team
 */
export function useGuestTags() {
  return useQuery({
    queryKey: ['guest-tags'],
    queryFn: async () => {
      const response = await apiClient.get<{ tags: GuestTag[] }>('/api/v1/guest-tags');
      return response.data.tags;
    },
    enabled: !!localStorage.getItem('token'),
  });
}

/**
 * Hook to create a guest tag
 */
export function useCreateGuestTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; color?: string }) => {
      const response = await apiClient.post<{ tag: GuestTag }>('/api/v1/guest-tags', data);
      return response.data.tag;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-tags'] });
    },
  });
}

/**
 * Hook to bulk-assign a tag to multiple guests (additive)
 */
export function useBulkAssignTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ tagId, guestIds }: { tagId: number; guestIds: number[] }) => {
      await apiClient.post(`/api/v1/guest-tags/${tagId}/bulk-assign`, { guest_ids: guestIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
    },
  });
}

/**
 * Hook to delete a guest tag
 */
export function useDeleteGuestTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tagId: number) => {
      await apiClient.delete(`/api/v1/guest-tags/${tagId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-tags'] });
      queryClient.invalidateQueries({ queryKey: ['all-guests'] });
    },
  });
}

// ─── Translations ──────────────────────────────────────────────────────────────

export function useInviteTranslations(inviteId: number | undefined) {
  return useQuery({
    queryKey: ['invite-translations', inviteId],
    queryFn: async () => {
      const response = await apiClient.get<{ translations: InviteTranslation[] }>(`/api/v1/invites/${inviteId}/translations`);
      return response.data.translations;
    },
    enabled: !!inviteId,
  });
}

export function useUpsertTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inviteId, locale, content }: { inviteId: number; locale: string; content: InviteTranslatableContent }) => {
      const response = await apiClient.put(`/api/v1/invites/${inviteId}/translations/${locale}`, { content });
      return response.data.translation as InviteTranslation;
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-translations', inviteId] });
      queryClient.invalidateQueries({ queryKey: ['invite', inviteId] });
    },
  });
}

export function useDeleteTranslation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inviteId, locale }: { inviteId: number; locale: string }) => {
      await apiClient.delete(`/api/v1/invites/${inviteId}/translations/${locale}`);
    },
    onSuccess: (_, { inviteId }) => {
      queryClient.invalidateQueries({ queryKey: ['invite-translations', inviteId] });
    },
  });
}

// ─── Allergy Data ──────────────────────────────────────────────────────────────

export function useAllergySummary(inviteId: number | undefined) {
  return useQuery({
    queryKey: ['allergy-summary', inviteId],
    queryFn: async () => {
      const response = await apiClient.get<AllergyDataSummary>(`/api/v1/invites/${inviteId}/allergy-summary`);
      return response.data;
    },
    enabled: !!inviteId,
  });
}

export function usePurgeAllergyData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: number) => {
      await apiClient.post(`/api/v1/invites/${inviteId}/allergy-data/purge`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allergy-summary'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useKeepAllergyData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inviteId, reason, notes }: { inviteId: number; reason: string; notes?: string }) => {
      await apiClient.post(`/api/v1/invites/${inviteId}/allergy-data/keep`, { reason, notes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allergy-summary'] });
    },
  });
}
